import { useState } from "react";

import { Box } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import {
  useDisclosure,
  useOffPlatformBookingPending,
  useShipperShipmentPageContext,
} from "hooks";

import { ClientAppEvent } from "../../../../constants";
import { CancelShipmentForm } from "../CancelShipmentForm";
import { CreateTemplateFromShipmentModal } from "../CreateTemplateFromShipmentModal";
import {
  isEligibleForOffPlatformBooking,
  MarkBookedOffPlatformModal,
} from "../MarkBookedOffPlatformModal";
import { UpdateShipmentEmailNotificationSettingsModal } from "../UpdateShipmentEmailNotificationSettingsModal";
import { ShareShipmentWithTeammatesModal } from "./components/ShareShipmentWithTeammatesModal";
import { ShipmentOverviewExpanded } from "./ShipmntOverviewExpanded";

import type { OffPlatformBookingSource } from "../MarkBookedOffPlatformModal";

export function ShipmentOverviewExpandedContainer() {
  const { shipment, onRefresh } = useShipperShipmentPageContext();
  const posthog = usePostHog();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: createTemplateModalOpen,
    onClose: onCreateTemplateModalClose,
    onOpen: onOpenCreateTemplateModal,
  } = useDisclosure(false);
  const [isShipmentCopyOpen, setIsShipmentCopyOpen] = useState<boolean>(false);
  const { pendingOffPlatformBooking } = useOffPlatformBookingPending({
    shipmentId: shipment?.info.id,
  });

  const {
    isOpen: updateEmailNotificationSettingsOpen,
    onClose: onCloseUpdateEmailNotificationSettings,
    onOpen: onOpenUpdateEmailNotificationSettings,
  } = useDisclosure(false);

  const {
    isOpen: shareShipmentWithTeammatesIsOpen,
    onClose: onCloseShareShipmentWithTeammates,
    onOpen: onOpenShareShipmentWithTeammates,
  } = useDisclosure(false);

  const {
    isOpen: markBookedOffPlatformModalOpen,
    onClose: onCloseMarkBookedOffPlatformModal,
    onOpen: onOpenMarkBookedOffPlatformModal,
  } = useDisclosure(false);
  const [offPlatformBookingSource, setOffPlatformBookingSource] =
    useState<OffPlatformBookingSource>("sidebar_menu");

  const openMarkBookedOffPlatform = (source: OffPlatformBookingSource) => {
    setOffPlatformBookingSource(source);
    onOpenMarkBookedOffPlatformModal();
  };

  const handleShipmentCopyClose = () => {
    setIsShipmentCopyOpen(false);
  };

  const handleShipmentCopyOpen = () => {
    posthog?.capture(ClientAppEvent.SHIPMENT_COPY_INITIATED, {
      source_shipment_id: shipment?.info?.id ?? undefined,
      source_shipment_type: shipment?.info?.type ?? undefined,
      source_shipment_status: shipment?.shipment_status ?? undefined,
    });
    setIsShipmentCopyOpen(true);
  };

  return (
    <Box>
      <ShipmentOverviewExpanded
        shipment={shipment}
        onOpenCancelShipment={onOpen}
        shipmentCopyFormOpen={isShipmentCopyOpen}
        onShipmentCopyClose={handleShipmentCopyClose}
        onShipmentCopyOpen={handleShipmentCopyOpen}
        onCreateTemplateFromShipmentOpen={onOpenCreateTemplateModal}
        onUpdateEmailNotificationSettingsOpen={
          onOpenUpdateEmailNotificationSettings
        }
        onShareShipmentWithTeammatesOpen={onOpenShareShipmentWithTeammates}
        onOpenMarkBookedOffPlatform={() =>
          openMarkBookedOffPlatform("sidebar_menu")
        }
        pendingOffPlatformBooking={pendingOffPlatformBooking}
      />
      {shipment !== undefined && (
        <CancelShipmentForm
          open={isOpen}
          onClose={onClose}
          shipment={shipment}
          onRecordOffPlatformBooking={
            isEligibleForOffPlatformBooking(shipment)
              ? () => openMarkBookedOffPlatform("cancellation_modal")
              : undefined
          }
          pendingOffPlatformBooking={pendingOffPlatformBooking}
        />
      )}
      {shipment !== undefined && (
        <MarkBookedOffPlatformModal
          shipment={shipment}
          modalOpen={markBookedOffPlatformModalOpen}
          onModalClose={onCloseMarkBookedOffPlatformModal}
          source={offPlatformBookingSource}
        />
      )}
      {shipment !== undefined && (
        <CreateTemplateFromShipmentModal
          shipment={shipment}
          onTemplateCreated={onCreateTemplateModalClose}
          onModalClose={onCreateTemplateModalClose}
          modalOpen={createTemplateModalOpen}
        />
      )}

      {shipment !== undefined && (
        <UpdateShipmentEmailNotificationSettingsModal
          shipment={shipment}
          onModalClose={() => {
            onCloseUpdateEmailNotificationSettings();
            if (onRefresh) {
              onRefresh();
            }
          }}
          onSettingsUpdated={onCloseUpdateEmailNotificationSettings}
          modalOpen={updateEmailNotificationSettingsOpen}
        />
      )}
      {shipment !== undefined && (
        <ShareShipmentWithTeammatesModal
          shipment={shipment}
          onModalClose={() => {
            onCloseShareShipmentWithTeammates();
            if (onRefresh) {
              onRefresh();
            }
          }}
          onTeammatesAdded={onCloseShareShipmentWithTeammates}
          modalOpen={shareShipmentWithTeammatesIsOpen}
        />
      )}
    </Box>
  );
}
